import { Plus } from 'lucide-react';

import RestaurantTable from '../../../shared/RestaurantTable';
import RestaurantTableActions from '../../../shared/RestaurantTableActions';
import { RestaurantComboDTO } from '../../../../../types/restaurant/RestaurantCatalogExtensionTypes';
import { formatMenuConfigurationMoney } from '../menuConfigurationUtils';

interface Props {
  combos: RestaurantComboDTO[];
  onCreate: () => void;
  onEdit: (combo: RestaurantComboDTO) => void;
  onToggleActive: (combo: RestaurantComboDTO) => void;
}

const CombosTable = ({ combos, onCreate, onEdit, onToggleActive }: Props) => {
  const columns = [
    {
      key: 'code',
      header: 'Código',
      className: 'w-32 font-mono text-xs text-bodydark2',
      cell: (row: RestaurantComboDTO) => row.code,
    },
    {
      key: 'name',
      header: 'Combo',
      cell: (row: RestaurantComboDTO) => (
        <div>
          <p className="font-medium text-black dark:text-white">{row.name}</p>
          {row.description && (
            <p className="mt-0.5 line-clamp-1 text-xs text-bodydark2">
              {row.description}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'price',
      header: 'Precio',
      className: 'text-sm font-semibold text-primary',
      cell: (row: RestaurantComboDTO) => formatMenuConfigurationMoney(row.price),
    },
    {
      key: 'items',
      header: 'Ítems',
      className: 'text-sm text-bodydark2',
      cell: (row: RestaurantComboDTO) => row.items.length,
    },
    {
      key: 'available',
      header: 'Disponible',
      cell: (row: RestaurantComboDTO) => (
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            row.available
              ? 'bg-success/15 text-success'
              : 'bg-warning/15 text-warning'
          }`}
        >
          {row.available ? 'Sí' : 'No'}
        </span>
      ),
    },
    {
      key: 'active',
      header: 'Estado',
      cell: (row: RestaurantComboDTO) => (
        <span
          className={`text-[11px] font-semibold ${
            row.active ? 'text-success' : 'text-danger'
          }`}
        >
          {row.active ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (row: RestaurantComboDTO) => (
        <RestaurantTableActions
          onEdit={() => onEdit(row)}
          onToggle={() => onToggleActive(row)}
          active={row.active}
        />
      ),
    },
  ];

  return (
    <div className="p-5 sm:p-7">
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
        >
          <Plus className="h-4 w-4" />
          Nuevo combo
        </button>
      </div>

      {combos.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-stroke p-8 text-center text-sm text-bodydark2 dark:border-strokedark">
          No hay combos con estos filtros.
        </p>
      ) : (
        <>
          <RestaurantTable
            columns={columns}
            rows={combos}
            rowKey={(row) => row.id}
            rowClassName={(row) => (row.active ? '' : 'opacity-60')}
            minWidth="min-w-[820px]"
          />

          <div className="space-y-3 md:hidden">
            {combos.map((combo) => (
              <div
                key={combo.id}
                className="rounded-xl border border-stroke p-4 dark:border-strokedark"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-black dark:text-white">
                      {combo.name}
                    </p>
                    <p className="mt-0.5 font-mono text-[11px] text-bodydark2">
                      {combo.code}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-primary">
                    {formatMenuConfigurationMoney(combo.price)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-bodydark2">
                  {combo.items.length}{' '}
                  {combo.items.length === 1 ? 'ítem' : 'ítems'} ·{' '}
                  {combo.available ? 'Disponible' : 'No disponible'} ·{' '}
                  {combo.active ? 'Activo' : 'Inactivo'}
                </p>
                <div className="mt-3">
                  <RestaurantTableActions
                    onEdit={() => onEdit(combo)}
                    onToggle={() => onToggleActive(combo)}
                    active={combo.active}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CombosTable;
